// frontend/src/components/CirclesPanel.js

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MinusCircle, PlusCircle } from "phosphor-react";
import { DEFAULT_CIRCLE_IMAGE } from "../utils/blank-image";

const INITIAL_VISIBLE_COUNT = 5;

const CirclesPanel = ({
  joinedCircles = [],
  readOnly = false, // Display Circles without allowing navigation
  self = false, // Is this the viewer's own profile?
  ownerName,
  titleOverride,
  maxIcons = 8, // Maximum available when readOnly
  hideTitle = false,
  frameless = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const items = Array.isArray(joinedCircles) ? joinedCircles : [];

  // Preserve the original readOnly limit.
  const availableItems = readOnly ? items.slice(0, maxIcons) : items;

  // Always begin with a maximum of five visible Circles.
  const visibleItems = expanded
    ? availableItems
    : availableItems.slice(0, INITIAL_VISIBLE_COUNT);

  const hiddenCount = Math.max(
    availableItems.length - INITIAL_VISIBLE_COUNT,
    0
  );

  const canExpand = availableItems.length > INITIAL_VISIBLE_COUNT;

  const ownerLabel =
    (typeof ownerName === "string" && ownerName.trim()) || "This user";

  const title =
    titleOverride ?? (self ? "Your Circles" : "Circles");

  const emptyText = self
    ? "You haven't joined any Circles yet"
    : `${ownerLabel} hasn't joined any Circles yet`;

  const wrapperClass = frameless ? "" : "rounded-2xl bg-white px-5 py-4";

  const getMemberCount = (circle) => {
    if (Array.isArray(circle?.members)) {
      return circle.members.length;
    }

    const possibleCounts = [
      circle?.member_count,
      circle?.members_count,
      circle?.num_members,
      circle?.memberCount,
    ];

    const count = possibleCounts.find(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== "" &&
        Number.isFinite(Number(value))
    );

    return count !== undefined ? Number(count) : null;
  };
  // Collapse again if the supplied Circle list changes.
  useEffect(() => {
    setExpanded(false);
  }, [joinedCircles, readOnly, maxIcons]);

  return (
    <section className={wrapperClass}>
      {!hideTitle && (
        <div className="mb-3 flex items-center">
          <h4 className="text-[16px] font-black tracking-[-0.02em] text-blue-600">
            {title}
          </h4>
        </div>
      )}

      {availableItems.length === 0 ? (
        <p className="text-sm text-gray-400">{emptyText}</p>
      ) : (
        <>
          <div className="space-y-2">
            {visibleItems.map((circle) => {
              const img =
                circle.image_url ||
                circle.icon_url ||
                DEFAULT_CIRCLE_IMAGE;

              const name =
                typeof circle?.name === "string"
                  ? circle.name
                  : typeof circle?.title === "string"
                    ? circle.title
                    : circle?.id
                      ? `Circle #${circle.id}`
                      : "Untitled Circle";
              const memberCount = getMemberCount(circle);

              const circleContent = (
                <>
                  <img
                    src={img}
                    alt={name}
                    className="h-11 w-11 shrink-0 rounded-full object-cover"
                    draggable={!readOnly}
                  />

                  <div className="min-w-0 flex-1">
                    <p
                      className={`truncate text-sm font-semibold text-gray-800 ${
                        readOnly
                          ? ""
                          : "transition group-hover:text-blue-600"
                      }`}
                    >
                      {name}
                    </p>

                    {memberCount !== null &&
                      memberCount !== undefined && (
                        <p className="truncate text-xs text-gray-500">
                          {memberCount}{" "}
                          {Number(memberCount) === 1
                            ? "member"
                            : "members"}
                        </p>
                      )}
                  </div>
                </>
              );

              if (readOnly) {
                return (
                  <div
                    key={circle.id ?? name}
                    className="flex select-none items-center gap-3"
                    title={name}
                  >
                    {circleContent}
                  </div>
                );
              }

              return (
                <Link
                  key={circle.id ?? name}
                  to={`/circles/${circle.id}`}
                  className="group flex items-center gap-3"
                  aria-label={`Open ${name}`}
                >
                  {circleContent}
                </Link>
              );
            })}
          </div>

          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              aria-expanded={expanded}
              className="
                mt-3
                inline-flex
                !min-h-0 !min-w-0
                items-center gap-1.5
                rounded-full
                bg-blue-50
                px-2.5 py-1.5
                text-xs font-semibold text-blue-600
                transition
                hover:bg-blue-100 hover:text-blue-700
              "
            >
              {expanded ? (
                <>
                  <MinusCircle size={17} weight="fill" />
                  Show less
                </>
              ) : (
                <>
                  <PlusCircle size={17} weight="fill" />
                  Show {hiddenCount} more
                </>
              )}
            </button>
          )}
        </>
      )}

      {readOnly &&
        availableItems.length > 0 &&
        items.length > availableItems.length && (
          <p className="mt-3 text-xs text-gray-400">
            Follow to view and open all circles.
          </p>
        )}
    </section>
  );
};

export default CirclesPanel;